/*
 * Cassaforte — suscripción: estado, pantalla de planes (prueba de 7 días) y avisos en la bóveda.
 * El pago ocurre en Stripe; aquí nunca se manejan datos de tarjetas.
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { getStore } from "../../services/store";

export interface Billing {
	status: "internal" | "none" | "trialing" | "active" | "past_due" | "canceled" | "unpaid" | "incomplete" | "incomplete_expired" | "paused";
	can_write: boolean;
	plan?: "monthly" | "yearly" | null;
	trial_end?: string | null;
	current_period_end?: string | null;
	cancel_at_period_end?: boolean;
	trial_available?: boolean;
}

const auth = () => ({ Authorization: "Token " + getStore().getState().user.token });

export async function fetchBilling(): Promise<Billing> {
	const r = await fetch("/billing/status", { headers: auth(), cache: "no-store" });
	if (!r.ok) throw new Error(String(r.status));
	return r.json();
}

async function post(path: string, body: object): Promise<{ url: string }> {
	const r = await fetch(path, { method: "POST", headers: { ...auth(), "Content-Type": "application/json" }, body: JSON.stringify(body) });
	if (!r.ok) throw new Error(String(r.status));
	return r.json();
}

export const setBillingLocale = (lang: string) => post("/billing/locale", { lang }).catch(() => undefined);

export const openPortal = (lang: string) => post("/billing/portal", { lang }).then((d) => { window.location.href = d.url; });

export const daysLeft = (iso?: string | null) => (iso ? Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000)) : 0);
export const fmtDate = (iso: string | null | undefined, lang: string) =>
	iso ? new Date(iso).toLocaleDateString(lang.startsWith("en") ? "en-US" : "es-ES", { day: "numeric", month: "long", year: "numeric" }) : "";

const XIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M6 6l12 12M18 6 6 18"/></svg>;
const Check = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 4 4 10-10"/></svg>;

/* ---------- Pantalla de planes ---------- */
export const Paywall = ({ billing, onClose, toast }: { billing: Billing | null; onClose: () => void; toast: (m: string) => void }) => {
	const { t, i18n } = useTranslation();
	const [plan, setPlan] = useState<"monthly" | "yearly">("yearly");
	const [busy, setBusy] = useState(false);
	const trial = !billing || billing.trial_available !== false;
	const firstCharge = new Date(Date.now() + 7 * 86400000).toISOString();
	const go = () => {
		setBusy(true);
		post("/billing/checkout", { plan, lang: i18n.language || "es" }).then(
			(d) => { window.location.href = d.url; },
			() => { setBusy(false); toast(t("CF_BILL_ERROR")); },
		);
	};
	const Plan = ({ k, price, per, note }: { k: "monthly" | "yearly"; price: string; per: string; note?: string }) => (
		<button type="button" className="cf-plan" role="radio" aria-checked={plan === k} onClick={() => setPlan(k)}>
			<span className="cf-plan-name">{t(k === "monthly" ? "CF_BILL_MONTHLY" : "CF_BILL_YEARLY")}{note && <em>{note}</em>}</span>
			<span className="cf-plan-price"><b>{price}</b> {per}</span>
		</button>
	);
	return (
		<div className="cf-scrim">
			<div className="cf-sheet" role="dialog" aria-modal="true" aria-labelledby="cfPayTitle">
				<div className="cf-shead">
					<button type="button" className="cf-sx" aria-label={t("CLOSE")} onClick={onClose}>{XIcon}</button>
					<h3 id="cfPayTitle">{trial ? t("CF_BILL_TRIAL_TITLE") : t("CF_BILL_CHOOSE_TITLE")}</h3>
					<p className="cf-lead">{trial ? t("CF_BILL_TRIAL_LEAD") : t("CF_BILL_CHOOSE_LEAD")}</p>
				</div>
				<div className="cf-sbody">
					<div className="cf-plans" role="radiogroup">
						<Plan k="yearly" price="$30" per={t("CF_BILL_PER_YEAR")} note={t("CF_BILL_SAVE")} />
						<Plan k="monthly" price="$3" per={t("CF_BILL_PER_MONTH")} />
					</div>
					<ul className="cf-checks">
						{trial && <li>{Check}<span>{t("CF_BILL_P1", { date: fmtDate(firstCharge, i18n.language || "es") })}</span></li>}
						<li>{Check}<span>{t("CF_BILL_P2")}</span></li>
						{trial && <li>{Check}<span>{t("CF_BILL_P3")}</span></li>}
						<li>{Check}<span>{t("CF_BILL_P4")}</span></li>
					</ul>
					<p className="cf-muted" style={{ fontSize: 13 }}>{t("CF_BILL_STRIPE")}</p>
				</div>
				<div className="cf-bar">
					<button type="button" className="cf-ghost" onClick={onClose}>{t("CF_BILL_LATER")}</button>
					<button type="button" className="cf-save" disabled={busy} onClick={go}>{busy ? t("CF_BILL_OPENING") : trial ? t("CF_BILL_START_TRIAL") : t("CF_BILL_CONTINUE")}</button>
				</div>
			</div>
		</div>
	);
};

/* ---------- Aviso bajo el encabezado ---------- */
export const BillingBanner = ({ billing, onChoose, toast }: { billing: Billing | null; onChoose: () => void; toast: (m: string) => void }) => {
	const { t, i18n } = useTranslation();
	if (!billing || billing.status === "internal" || billing.status === "active") return null;
	const lang = i18n.language || "es";
	const portal = () => openPortal(lang).catch(() => toast(t("CF_BILL_ERROR")));
	if (billing.status === "trialing") {
		const n = daysLeft(billing.trial_end);
		return (
			<div className="cf-banner cf-banner-info">
				<span>{billing.cancel_at_period_end ? t("CF_BILL_TRIAL_ENDS", { date: fmtDate(billing.trial_end, lang) }) : t("CF_BILL_TRIAL_LEFT", { count: n, date: fmtDate(billing.trial_end, lang) })}</span>
			</div>
		);
	}
	if (billing.status === "past_due") {
		return (
			<div className="cf-banner cf-banner-warn">
				<span>{t("CF_BILL_PAST_DUE")}</span>
				<button type="button" className="cf-btn" onClick={portal}>{t("CF_BILL_UPDATE_PAYMENT")}</button>
			</div>
		);
	}
	if (!billing.can_write) {
		return (
			<div className="cf-banner cf-banner-warn">
				<span>{t("CF_BILL_READONLY")}</span>
				<button type="button" className="cf-btn" onClick={onChoose}>{t("CF_BILL_CHOOSE")}</button>
			</div>
		);
	}
	return null;
};

/* ---------- Sección en Cuenta ---------- */
export const SubscriptionSection = ({ billing, onChoose, toast }: { billing: Billing | null; onChoose: () => void; toast: (m: string) => void }) => {
	const { t, i18n } = useTranslation();
	const lang = i18n.language || "es";
	if (!billing) return null;
	const planName = billing.plan === "yearly" ? t("CF_BILL_YEARLY") : billing.plan === "monthly" ? t("CF_BILL_MONTHLY") : "";
	let text = "";
	if (billing.status === "internal") text = t("CF_BILL_INTERNAL");
	else if (billing.status === "trialing") text = t("CF_BILL_ACC_TRIAL", { plan: planName, date: fmtDate(billing.trial_end, lang) });
	else if (billing.status === "active") text = billing.cancel_at_period_end ? t("CF_BILL_ACC_ENDS", { plan: planName, date: fmtDate(billing.current_period_end, lang) }) : t("CF_BILL_ACC_ACTIVE", { plan: planName, date: fmtDate(billing.current_period_end, lang) });
	else if (billing.status === "past_due") text = t("CF_BILL_PAST_DUE");
	else text = t("CF_BILL_ACC_NONE");
	const manage = ["trialing", "active", "past_due", "canceled", "unpaid"].includes(billing.status);
	return (
		<section className="cf-section">
			<h4>{t("CF_BILL_SECTION")}</h4>
			<p className="cf-muted">{text}</p>
			<div className="cf-dactions" style={{ marginTop: 0 }}>
				{!billing.can_write && <button type="button" className="cf-btn" onClick={onChoose}>{t("CF_BILL_CHOOSE")}</button>}
				{manage && <button type="button" className="cf-btn" onClick={() => openPortal(lang).catch(() => toast(t("CF_BILL_ERROR")))}>{t("CF_BILL_MANAGE")}</button>}
			</div>
		</section>
	);
};
